import { useEffect, useRef, useState } from 'react';
import * as signalR from '@microsoft/signalr';
import { authService } from '../services/authService';

const SIGNALR_HUB_URL = import.meta.env.VITE_NOTIFICATIONS_API_URL
  ? `${import.meta.env.VITE_NOTIFICATIONS_API_URL.replace('/api', '')}/hubs/notifications`
  : 'https://notifications.agendario.com.br/hubs/notifications';

/** Custom logger que prefixa todas as mensagens do SignalR com [SignalR] */
const signalRLogger = {
  log(logLevel, message) {
    const taggedMessage = `[SignalR] ${message}`;
    if (logLevel >= signalR.LogLevel.Error) {
      console.error(taggedMessage);
    } else if (logLevel >= signalR.LogLevel.Warning) {
      console.warn(taggedMessage);
    }
  }
};

/**
 * Hook para conectar ao SignalR Hub de notificações
 * Isolado e seguro - não quebra o app em caso de erro
 */
export function useNotifications(onNotificationReceived) {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const connectionRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  useEffect(() => {
    const token = authService.getToken();
    if (!token) {
      console.warn('[SignalR] No token available, skipping connection');
      return;
    }

    // Criar conexão SignalR
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(SIGNALR_HUB_URL, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: () => {
          // Retry delays: 2s, 5s, 10s, 30s, then 60s
          return [2000, 5000, 10000, 30000, 60000];
        }
      })
      .withServerTimeout(90000) // ✅ 90s timeout (NOVO - evita desconexões abruptas)
      .configureLogging(signalRLogger)
      .build();

    connectionRef.current = connection;

    // Event handlers
    connection.on('ReceiveNotification', (notification) => {
      console.log('[Notifications] Received:', notification);
      if (onNotificationReceived) {
        onNotificationReceived(notification);
      }
    });

    connection.onclose((error) => {
      console.warn('[SignalR] Connection closed:', error?.message);
      setIsConnected(false);
      setConnectionError(error?.message || 'Connection closed');
    });

    connection.onreconnecting((error) => {
      console.warn('[SignalR] Reconnecting:', error?.message);
      setIsConnected(false);
    });

    connection.onreconnected((connectionId) => {
      console.log('[SignalR] Reconnected:', connectionId);
      setIsConnected(true);
      setConnectionError(null);
    });

    // Iniciar conexão
    const startConnection = async () => {
      try {
        await connection.start();
        console.log('[SignalR] Connected successfully');
        setIsConnected(true);
        setConnectionError(null);
      } catch (error) {
        console.warn('[SignalR] Connection failed:', error.message);
        setIsConnected(false);
        setConnectionError(error.message);
        
        // Tentar reconectar após 10s
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log('[SignalR] Attempting manual reconnect...');
          startConnection();
        }, 10000);
      }
    };

    startConnection();

    // Cleanup
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (connectionRef.current) {
        connectionRef.current.stop().catch((err) => {
          console.warn('[SignalR] Error stopping connection:', err.message);
        });
      }
    };
  }, [onNotificationReceived]);

  return {
    isConnected,
    connectionError,
    connection: connectionRef.current,
  };
}
